import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Divider } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloseOutlined, StorefrontOutlined, DeleteOutlineOutlined,
  TouchAppOutlined, OpenInNewOutlined,
} from '@mui/icons-material';
import { InspSectionTitle, InspRow, InspInput, ToggleRow, selectStyle } from '../ui/primitives';
import { MediaField } from '../MediaField';

const EX_COLOR = '#0d7a5f';

// ── Small color swatch input ──────────────────────────────────────────────────
function ColorRow({ label, value, onChange }) {
  return (
    <InspRow label={label}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <input
          type="color"
          value={value || '#1a1a2e'}
          onChange={e => onChange(e.target.value)}
          style={{ width: 28, height: 28, border: '1px solid #e4e9f0', borderRadius: 4, padding: 2, cursor: 'pointer', background: 'none', flexShrink: 0 }}
        />
        <InspInput value={value || ''} onChange={onChange} placeholder="#1a1a2e" />
      </Box>
    </InspRow>
  );
}

/**
 * Left panel shown when an exhibitor card is selected in ExhibitorCanvas.
 *
 * Props:
 *   exhibitor       — Exhibitor document (local state)
 *   onUpdate        — (patch) => void   — immediate local update
 *   onDelete        — () => void        — remove from hall
 *   onEditBooth     — () => void        — open ExhibitorBoothEditor
 *   onClose         — () => void
 */
export function ExhibitorInspector({ exhibitor, onUpdate, onDelete, onEditBooth, onClose }) {
  if (!exhibitor) return null;
  const upd = patch => onUpdate({ ...exhibitor, ...patch });

  const hotspotCount = exhibitor.hotspots?.length ?? 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Header ── */}
      <Box sx={{
        px: 2, py: 1.75, borderBottom: '1px solid #e8edf2',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 26, height: 26, borderRadius: 1.5,
            background: alpha(EX_COLOR, 0.1),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: EX_COLOR,
          }}>
            <StorefrontOutlined sx={{ fontSize: 15 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a1a2e', lineHeight: 1 }}>
              Booth Settings
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: '#b0bac8', mt: 0.2 }} noWrap>
              {exhibitor.name}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#9ea8b3' }}>
          <CloseOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* ── Scrollable body ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>

        {/* ── Identity ── */}
        <InspSectionTitle label="Identity" color={EX_COLOR} />

        <InspRow label="Name">
          <InspInput value={exhibitor.name || ''} onChange={v => upd({ name: v })} placeholder="Company name" />
        </InspRow>

        <InspRow label="Slug">
          <InspInput value={exhibitor.slug || ''} onChange={v => upd({ slug: v.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder="company-name" />
        </InspRow>

        <InspRow label="Tagline">
          <InspInput value={exhibitor.tagline || ''} onChange={v => upd({ tagline: v })} placeholder="Short tagline" />
        </InspRow>

        <InspRow label="Category">
          <InspInput value={exhibitor.category || ''} onChange={v => upd({ category: v })} placeholder="e.g. Gold Sponsor" />
        </InspRow>

        <InspRow label="Order">
          <input
            type="number" min={0}
            value={exhibitor.order ?? 0}
            onChange={e => upd({ order: parseInt(e.target.value) || 0 })}
            style={{
              width: '100%', border: '1px solid #e4e9f0', borderRadius: 6,
              padding: '6px 10px', fontSize: '12px', outline: 'none',
              fontFamily: 'inherit', background: '#f8f9fb', boxSizing: 'border-box',
            }}
          />
        </InspRow>

        <ToggleRow label="Active" value={exhibitor.isActive !== false} onChange={v => upd({ isActive: v })} />

        {/* ── Card Assets ── */}
        <InspSectionTitle label="Card Assets" color="#e67e22" />

        <InspRow label="Booth Thumbnail">
          <MediaField value={exhibitor.boothThumbnail || ''} onChange={v => upd({ boothThumbnail: v || null })} mediaType="image" />
        </InspRow>

        <InspRow label="Logo">
          <MediaField value={exhibitor.logoUrl || ''} onChange={v => upd({ logoUrl: v || null })} mediaType="image" />
        </InspRow>

        <ColorRow label="Booth Color" value={exhibitor.boothBgColor} onChange={v => upd({ boothBgColor: v })} />

        {/* ── Booth Page ── */}
        <InspSectionTitle label="Booth Page" color="#1075BA" />

        <InspRow label="Background Image">
          <MediaField value={exhibitor.boothBgImage || ''} onChange={v => upd({ boothBgImage: v || null })} mediaType="image" />
        </InspRow>

        <InspRow label="Background Video">
          <MediaField value={exhibitor.boothBgVideo || ''} onChange={v => upd({ boothBgVideo: v || null })} mediaType="video" />
        </InspRow>

        <ColorRow label="Accent Color" value={exhibitor.accentColor} onChange={v => upd({ accentColor: v })} />

        {/* ── Contact ── */}
        <InspSectionTitle label="Contact" color="#9ea8b3" />

        <InspRow label="Website">
          <InspInput value={exhibitor.website || ''} onChange={v => upd({ website: v || null })} placeholder="https://…" />
        </InspRow>

        <InspRow label="Email">
          <InspInput value={exhibitor.email || ''} onChange={v => upd({ email: v || null })} placeholder="contact@…" />
        </InspRow>

        <InspRow label="Phone">
          <InspInput value={exhibitor.phone || ''} onChange={v => upd({ phone: v || null })} placeholder="+1 555 …" />
        </InspRow>

        {/* ── Hotspots summary ── */}
        <InspSectionTitle label="Hotspots" color="#8b4fcc" />

        <Box
          onClick={onEditBooth}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderRadius: '8px', px: 1.5, py: 1, mb: 1.5,
            border: '1.5px solid #e4e9f0', bgcolor: '#f8f9fb',
            cursor: 'pointer', transition: 'all 0.12s',
            '&:hover': { borderColor: '#8b4fcc', bgcolor: alpha('#8b4fcc', 0.04) },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TouchAppOutlined sx={{ fontSize: 15, color: '#8b4fcc' }} />
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#3c4555', lineHeight: 1 }}>
                {hotspotCount} hotspot{hotspotCount !== 1 ? 's' : ''}
              </Typography>
              <Typography sx={{ fontSize: '0.63rem', color: '#9ea8b3' }}>
                Double-click card to edit
              </Typography>
            </Box>
          </Box>
          <OpenInNewOutlined sx={{ fontSize: 13, color: '#b0bac8' }} />
        </Box>

      </Box>

      {/* ── Footer: delete ── */}
      <Box sx={{ px: 2, pb: 2, flexShrink: 0 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Box
          onClick={onDelete}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.75,
            borderRadius: '7px', px: 1.5, py: 0.85, cursor: 'pointer',
            border: '1.5px solid #fca5a5', bgcolor: alpha('#ef4444', 0.04),
            color: '#dc2626', fontSize: '0.78rem', fontWeight: 600,
            transition: 'all 0.12s',
            '&:hover': { bgcolor: alpha('#ef4444', 0.09), borderColor: '#ef4444' },
          }}
        >
          <DeleteOutlineOutlined sx={{ fontSize: 15 }} />
          Remove from hall
        </Box>
      </Box>
    </Box>
  );
}